/**
 * Embassy lookup service
 * 
 * Note: This is a simplified implementation. In production, you would:
 * 1. Use a proper embassy directory API
 * 2. Cache embassy information
 * 3. Support multiple embassies per country
 * 4. Include full contact details, addresses, hours, etc.
 */

export type EmbassyInfo = {
  country: string;
  countryCode: string;
  embassyName: string;
  phone: string;
  email?: string;
  address?: string;
  website?: string;
  emergencyPhone?: string;
};

/**
 * Get embassy information for a country
 * This is a placeholder - in production, you'd use an embassy directory API
 */
export const getEmbassyInfo = async (
  citizenshipCountryCode: string,
  destinationCountryCode?: string
): Promise<EmbassyInfo | null> => {
  try {
    // Placeholder embassy data
    // In production, this would query an embassy directory API
    const embassyDatabase: Record<string, Partial<EmbassyInfo>> = {
      US: {
        country: 'United States',
        countryCode: 'US',
        embassyName: 'U.S. Embassy',
        phone: '+1 202-501-4444',
        emergencyPhone: '+1 202-501-4444',
        website: 'https://travel.state.gov',
      },
      GB: {
        country: 'United Kingdom',
        countryCode: 'GB',
        embassyName: 'British Embassy',
        phone: '+44 20 7008 5000',
        emergencyPhone: '+44 20 7008 5000',
      },
      CA: {
        country: 'Canada',
        countryCode: 'CA',
        embassyName: 'Canadian Embassy',
        phone: '+1 613-996-8885',
        emergencyPhone: '+1 613-996-8885',
      },
      AU: {
        country: 'Australia',
        countryCode: 'AU',
        embassyName: 'Australian Embassy',
        phone: '+61 2 6261 3300',
        emergencyPhone: '+61 2 6261 3300',
      },
      FR: {
        country: 'France',
        countryCode: 'FR',
        embassyName: 'French Embassy',
        phone: '+33 1 43 17 19 00',
        emergencyPhone: '+33 1 43 17 19 00',
      },
      DE: {
        country: 'Germany',
        countryCode: 'DE',
        embassyName: 'German Embassy',
        phone: '+49 30 5000 0',
        emergencyPhone: '+49 30 5000 0',
      },
      PT: {
        country: 'Portugal',
        countryCode: 'PT',
        embassyName: 'Portuguese Embassy',
        phone: '+351 21 770 0000',
        emergencyPhone: '+351 21 770 0000',
      },
    };

    const embassy = embassyDatabase[citizenshipCountryCode.toUpperCase()];
    if (!embassy) {
      return null;
    }

    return {
      country: embassy.country || citizenshipCountryCode,
      countryCode: embassy.countryCode || citizenshipCountryCode,
      embassyName: embassy.embassyName || `${citizenshipCountryCode} Embassy`,
      phone: embassy.phone || '+1 202-501-4444', // Default to US State Department
      email: embassy.email,
      address: embassy.address,
      website: embassy.website,
      emergencyPhone: embassy.emergencyPhone || embassy.phone,
    };
  } catch (error) {
    console.error('[Embassy] Error getting embassy info:', error);
    return null;
  }
};

/**
 * Get embassy phone number for emergency calls
 */
export const getEmbassyEmergencyPhone = async (
  citizenshipCountryCode: string
): Promise<string | null> => {
  const embassy = await getEmbassyInfo(citizenshipCountryCode);
  return embassy?.emergencyPhone || embassy?.phone || null;
};

/**
 * Extract country code from country name or code
 */
export const normalizeCountryCode = (country: string): string => {
  // This is a simplified version - in production, you'd use a proper country code mapping
  const countryMap: Record<string, string> = {
    'United States': 'US',
    'USA': 'US',
    'United Kingdom': 'GB',
    'UK': 'GB',
    'Canada': 'CA',
    'Australia': 'AU',
    'France': 'FR',
    'Germany': 'DE',
    'Portugal': 'PT',
    'Spain': 'ES',
    'Italy': 'IT',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Poland': 'PL',
    'Czech Republic': 'CZ',
    'Greece': 'GR',
    'Ireland': 'IE',
    'New Zealand': 'NZ',
    'Japan': 'JP',
    'China': 'CN',
    'South Korea': 'KR',
    'India': 'IN',
    'Brazil': 'BR',
    'Mexico': 'MX',
    'Argentina': 'AR',
  };

  const normalized = country.trim();
  
  // Check if it's already a country code
  if (normalized.length === 2) {
    return normalized.toUpperCase();
  }

  // Check country map
  return countryMap[normalized] || normalized.toUpperCase().substring(0, 2);
};





